<script>
  import { onDestroy } from 'svelte';
  import ParticipantDetails from '$lib/ParticipantDetails.svelte';
  import FeatureInfo from '$lib/FeatureInfo.svelte';
  import { selectedStudy, selectedSite, selectedParticipant } from '$stores/selectionStore';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import { faRotate } from '@fortawesome/pro-light-svg-icons';
  import { formatToLocalDateTime } from '$utils/dateUtils.js';
  import { exportToExcel, generateExportFilename } from '$utils/export.js';
  import Toolbar from '$lib/Toolbar.svelte';
  import Footer from '$lib/Footer.svelte';
  import StudyHeader from '$lib/StudyHeader.svelte';
  import StudyDetails from '$lib/StudyDetails.svelte';
  import SiteDetails from '$lib/SiteDetails.svelte';

  const API_BASE_URL = '/api/proxy';
  const demoMode = import.meta.env.VITE_DEMO_MODE === 'true';

  const RESULT_LABELS = { passed: 'Passed', failed: 'Failed', inProgress: 'In progress' };
  const RESULT_CLASSES = {
    passed: 'text-green-700 dark:text-green-400',
    failed: 'text-red-700 dark:text-red-400',
    inProgress: 'text-gray-500 dark:text-gray-400',
  };

  let report = null;
  let isLoading = true;
  let isExporting = false;
  let selectedType = 'ALL';
  let inRange = true;
  // null until the first response fills them with the study's configured defaults
  let threshold = null;
  let lifetimeDays = null;
  let groupBy = 'site';
  let resultFilter = 'all';
  let abortController = null;

  $: excludedSiteIds = report?.excludedSiteIds || [];
  $: selectedSiteExcluded =
    excludedSiteIds.length > 0 &&
    $selectedSite?.siteId != null &&
    excludedSiteIds.includes(String($selectedSite.siteId));

  $: groupOptions = $selectedParticipant
    ? [{ value: 'sensor', label: 'Sensor' }]
    : $selectedSite
      ? [
          { value: 'participant', label: 'Participant' },
          { value: 'sensor', label: 'Sensor' },
        ]
      : [
          { value: 'site', label: 'Site' },
          { value: 'participant', label: 'Participant' },
          { value: 'sensor', label: 'Sensor' },
        ];
  $: if (!groupOptions.some((o) => o.value === groupBy)) groupBy = groupOptions[0].value;

  $: earlyLabel = `Failed < ${formatHours(report?.earlyFailureHours ?? 24)}`;

  $: rows =
    groupBy === 'site'
      ? report?.sites || []
      : groupBy === 'participant'
        ? report?.participants || []
        : (report?.sensors || []).filter((sensor) => matchesResultFilter(sensor, resultFilter));

  $: featureDescription = `Counts per site, participant and sensor how many CGM sensors reached the completion threshold.
  *Completion*: readings received over the readings expected for a full sensor (${report?.perDayExpected ?? '…'} readings/day × ${report?.lifetimeDays ?? '…'}-day lifetime = ${report?.expectedReadings ?? '…'} readings).
  A sensor *passed* once its completion reaches ${report?.thresholdPercent ?? '…'}%. Below that it *failed* once it ended, otherwise it is *in progress*. A sensor ended when its activity is completed, its lifetime has elapsed since the first reading, or the participant started a newer sensor after its last reading.
  *${earlyLabel}*: failed sensors whose readings span less than ${formatHours(report?.earlyFailureHours ?? 24)} (first to last reading), typically sensors that failed shortly after being started.
  *Failure rate*: failed over finished (passed + failed) sensors. Threshold and lifetime default to the study's CGM compliance threshold and device lifetime; one lifetime applies to all sensors of the report.`;

  function matchesResultFilter(sensor, filter) {
    if (filter === 'all') return true;
    if (filter === 'earlyFailure') return sensor.earlyFailure === true;
    return sensor.result === filter;
  }

  function formatHours(hours) {
    return hours % 24 === 0 ? `${hours / 24} day${hours === 24 ? '' : 's'}` : `${hours} h`;
  }

  function formatDuration(days) {
    const d = Number(days) || 0;
    return `${Math.floor(d)}d ${Math.floor((d % 1) * 24)}h ${Math.floor((((d % 1) * 24) % 1) * 60)}m`;
  }

  function resultLabel(sensor) {
    if (sensor.earlyFailure) return `Failed (< ${formatHours(report?.earlyFailureHours ?? 24)})`;
    return RESULT_LABELS[sensor.result] ?? sensor.result;
  }

  let lastScope = null;
  $: if ($selectedStudy) {
    const scope = `${$selectedStudy._id}|${$selectedSite?._id ?? ''}|${$selectedParticipant?._id ?? ''}`;
    if (scope !== lastScope) {
      if (lastScope?.split('|')[0] !== $selectedStudy._id) {
        threshold = null;
        lifetimeDays = null;
      }
      lastScope = scope;
      fetchReport();
    }
  }

  onDestroy(() => abortController?.abort());

  async function fetchReport() {
    abortController?.abort();
    abortController = new AbortController();
    isLoading = true;
    try {
      const params = new URLSearchParams({
        studyGUID: $selectedStudy._id,
        subjectStatus: selectedType,
        inRange: String(inRange),
      });
      if ($selectedParticipant) params.set('subjectGUID', $selectedParticipant._id);
      if ($selectedSite) params.set('siteGUID', $selectedSite._id);
      if (threshold !== null && threshold !== '') params.set('threshold', String(threshold));
      if (lifetimeDays !== null && lifetimeDays !== '') params.set('lifetimeDays', String(lifetimeDays));

      const res = await fetch(`${API_BASE_URL}/glucoseReadings/cgmCompletion?${params.toString()}`, {
        signal: abortController.signal,
      });
      if (!res.ok) throw new Error(`Failed to fetch CGM completion: ${await res.text()}`);
      report = await res.json();
      threshold = report.thresholdPercent;
      lifetimeDays = report.lifetimeDays;
      isLoading = false;
    } catch (err) {
      if (err?.name === 'AbortError') return;
      console.error('Error fetching CGM completion:', err);
      report = null;
      isLoading = false;
    }
  }

  function formatRate(rate) {
    return rate === null || rate === undefined ? '–' : `${rate}%`;
  }

  function exportRow(row) {
    const counts = {
      Sensors: row.sensors,
      Passed: row.passed,
      Failed: row.failed,
      [earlyLabel]: row.earlyFailures,
      'In Progress': row.inProgress,
      'Failure Rate %': row.failureRate ?? '',
    };
    if (groupBy === 'site') {
      return { Site: row.siteId, Country: row.country, Participants: row.participants, ...counts };
    }
    if (groupBy === 'participant') {
      return { Site: row.siteId, Subject: row.subjectId, Status: row.subjectStatus, Country: row.country, ...counts };
    }
    return {
      Site: row.siteId || '',
      Subject: row.subjectId || '',
      Status: row.subjectStatus || '',
      Device: row.device || '',
      Sensor: row.sensor || '',
      'From (Local)': row.minRead ? formatToLocalDateTime(row.minRead, row.timezone) : '',
      'To (Local)': row.maxRead ? formatToLocalDateTime(row.maxRead, row.timezone) : '',
      'Duration (days)': row.daysBetween ? parseFloat(Number(row.daysBetween).toFixed(2)) : 0,
      Readings: row.count ?? 0,
      'Completion %': row.wearpercentage ?? 0,
      Result: resultLabel(row),
    };
  }

  async function handleExportExcel() {
    if (!report) return;
    try {
      isExporting = true;
      const filename = generateExportFilename({
        protocolNumber: demoMode ? 'DEMO-0001' : $selectedStudy?.protocolNumber,
        siteId: $selectedSite?.siteId,
        subjectId: $selectedParticipant?.subjectId,
        reportType: `CGM_Completion_by_${groupBy}${groupBy === 'sensor' && resultFilter !== 'all' ? `_${resultFilter}` : ''}`,
      });
      await exportToExcel(rows.map(exportRow), filename);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
    } finally {
      isExporting = false;
    }
  }
</script>

{#if isExporting}
  <div class="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
    <div class="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg flex flex-col items-center">
      <div class="loader mb-4"></div>
      <p class="text-sm text-gray-700 dark:text-gray-300">Preparing Excel export...</p>
    </div>
  </div>
{/if}

<StudyHeader />

<FeatureInfo title="CGM Completion" description={featureDescription} marginBottom="mb-0" />

{#if $selectedParticipant}
  <ParticipantDetails showOptionsMenu={false} />
{:else if $selectedSite}
  <SiteDetails showOptionsMenu={false} />
{:else if $selectedStudy}
  <StudyDetails showOptionsMenu={false} />
{/if}

{#if selectedSiteExcluded}
  <div class="mx-4 mt-2 px-3 py-2 rounded border border-amber-400 bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 text-sm" role="alert">
    Site {$selectedSite.siteId} is excluded from study-level reports by the study
    configuration (test/UAT site), so its sensors are not shown here.
  </div>
{:else if excludedSiteIds.length > 0}
  <div class="mx-4 mt-2 px-3 py-2 rounded border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300 text-xs">
    Note: site{excludedSiteIds.length > 1 ? 's' : ''} {excludedSiteIds.join(', ')}
    {excludedSiteIds.length > 1 ? 'are' : 'is'} excluded from this report by the study configuration.
  </div>
{/if}

<Toolbar title="CGM Completion" itemCountTitle="Sensors" itemCount={report?.summary?.sensors ?? 0}>
  <!-- Toolbar aligns its items by text baseline; center the form controls within one item instead. -->
  <div class="flex items-center gap-4">
    <div class="flex items-center gap-2">
      Group by:
      <select
        bind:value={groupBy}
        class="form-select h-6 border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
      >
        {#each groupOptions as option (option.value)}
          <option value={option.value}>{option.label}</option>
        {/each}
      </select>
    </div>
    {#if groupBy === 'sensor'}
      <div class="flex items-center gap-2">
        Result:
        <select
          bind:value={resultFilter}
          class="form-select h-6 border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
        >
          <option value="all">All</option>
          <option value="failed">Failed</option>
          <option value="earlyFailure">{earlyLabel}</option>
          <option value="passed">Passed</option>
          <option value="inProgress">In progress</option>
        </select>
      </div>
    {/if}
    {#if !$selectedParticipant}
      <div class="flex items-center gap-2">
        Status:
        <select
          bind:value={selectedType}
          on:change={fetchReport}
          class="form-select h-6 border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
        >
          <option value="ALL" selected>All</option>
          <option value="COMPD">Completed</option>
          <option value="CONS">Consented</option>
          <option value="DISC">Discontinued</option>
          <option value="INIT">Initiated</option>
          <option value="SCFA">Screen Failed</option>
          <option value="SCRE">Screened</option>
          <option value="ENRO">Screened/Enrolled</option>
        </select>
      </div>
    {/if}
    <div class="flex items-center gap-2">
      Threshold %:
      <input
        type="number"
        min="0"
        max="100"
        bind:value={threshold}
        on:change={fetchReport}
        class="form-input h-6 w-16 border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white"
      />
    </div>
    <div class="flex items-center gap-2">
      Lifetime (days):
      <input
        type="number"
        min="1"
        step="0.5"
        bind:value={lifetimeDays}
        on:change={fetchReport}
        class="form-input h-6 w-16 border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white"
      />
    </div>
    <div class="flex items-center print:hidden">
      <input
        type="checkbox"
        bind:checked={inRange}
        on:change={fetchReport}
        class="w-4 h-4 text-red-600 bg-gray-100 border-gray-300 rounded focus:ring-red-500 dark:focus:ring-red-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
      />
      <span class="ms-2">Valid Device Range</span>
    </div>
    <button on:click={fetchReport} class="pagination flex items-center p-0" title="Refresh">
      <FontAwesomeIcon icon={faRotate} />
    </button>
  </div>
</Toolbar>

{#if isLoading}
  <div class="flex flex-col justify-center items-center h-64 bg-stone-100/50 dark:bg-gray-725 mx-1">
    <div class="loader mb-4"></div>
    <p class="text-xs justify-center text-gray-700 dark:text-gray-300">preparing CGM completion report</p>
  </div>
{:else if report}
  <div class="grid grid-cols-2 md:grid-cols-6 gap-2 px-1 py-2 text-sm text-gray-800 dark:text-slate-300">
    <div class="bg-stone-50 dark:bg-gray-725 rounded p-2">
      <div class="text-xs text-gray-500 dark:text-gray-400">Sensors</div>
      <div class="text-lg font-semibold">{report.summary.sensors}</div>
    </div>
    <div class="bg-stone-50 dark:bg-gray-725 rounded p-2">
      <div class="text-xs text-gray-500 dark:text-gray-400">Passed (≥ {report.thresholdPercent}%)</div>
      <div class="text-lg font-semibold {RESULT_CLASSES.passed}">{report.summary.passed}</div>
    </div>
    <div class="bg-stone-50 dark:bg-gray-725 rounded p-2">
      <div class="text-xs text-gray-500 dark:text-gray-400">Failed</div>
      <div class="text-lg font-semibold {RESULT_CLASSES.failed}">{report.summary.failed}</div>
    </div>
    <div class="bg-stone-50 dark:bg-gray-725 rounded p-2">
      <div class="text-xs text-gray-500 dark:text-gray-400">{earlyLabel}</div>
      <div class="text-lg font-semibold {RESULT_CLASSES.failed}">{report.summary.earlyFailures}</div>
    </div>
    <div class="bg-stone-50 dark:bg-gray-725 rounded p-2">
      <div class="text-xs text-gray-500 dark:text-gray-400">In progress</div>
      <div class="text-lg font-semibold {RESULT_CLASSES.inProgress}">{report.summary.inProgress}</div>
    </div>
    <div class="bg-stone-50 dark:bg-gray-725 rounded p-2">
      <div class="text-xs text-gray-500 dark:text-gray-400">Failure rate</div>
      <div class="text-lg font-semibold">{formatRate(report.summary.failureRate)}</div>
    </div>
  </div>

  <div class="px-1 pt-0">
    <table class="min-w-full border-collapse table-auto">
      <thead>
        <tr class="text-sm text-black dark:text-slate-300 bg-stone-200/50 dark:bg-gray-800/90">
          <th class="px-4 py-2 text-left bold">Site</th>
          {#if groupBy === 'site'}
            <th class="px-4 py-2 text-left bold">Country</th>
            <th class="px-4 py-2 text-left bold">Participants</th>
          {:else}
            <th class="px-4 py-2 text-left bold">Subject</th>
            <th class="px-4 py-2 text-left bold">Status</th>
          {/if}
          {#if groupBy === 'sensor'}
            <th class="px-4 py-2 text-left bold">Device</th>
            <th class="px-4 py-2 text-left bold">Sensor</th>
            <th class="px-4 py-2 text-left bold w-48">Readings From - To (Local)</th>
            <th class="px-4 py-2 text-left bold w-32">Duration</th>
            <th class="px-4 py-2 text-left bold">Readings</th>
            <th class="px-4 py-2 text-left bold">Completion</th>
            <th class="px-4 py-2 text-left bold">Result</th>
          {:else}
            <th class="px-4 py-2 text-left bold">Sensors</th>
            <th class="px-4 py-2 text-left bold">Passed</th>
            <th class="px-4 py-2 text-left bold">Failed</th>
            <th class="px-4 py-2 text-left bold">{earlyLabel}</th>
            <th class="px-4 py-2 text-left bold">In progress</th>
            <th class="px-4 py-2 text-left bold">Failure rate</th>
          {/if}
        </tr>
      </thead>
      <tbody>
        {#each rows as row, index}
          <tr
            class="text-sm hover:bg-white dark:hover:bg-gray-775 text-gray-800 dark:text-slate-300 {index % 2 === 0
              ? 'bg-stone-50 dark:bg-gray-725'
              : 'bg-stone-75 dark:bg-gray-750'}"
          >
            <td class="px-4 py-2">{row.siteId ?? ''}</td>
            {#if groupBy === 'site'}
              <td class="px-4 py-2">{row.country ?? ''}</td>
              <td class="px-4 py-2">{row.participants}</td>
            {:else}
              <td class="px-4 py-2">{row.subjectId ?? ''}</td>
              <td class="px-4 py-2">{row.subjectStatus ?? ''}</td>
            {/if}
            {#if groupBy === 'sensor'}
              <td class="px-4 py-2">{row.device ?? ''}</td>
              <td class="px-4 py-2">{row.sensor ?? ''}</td>
              <td class="px-4 py-2 w-52">
                {row.minRead ? formatToLocalDateTime(row.minRead, row.timezone) : ''}
                <br />
                {row.maxRead ? formatToLocalDateTime(row.maxRead, row.timezone) : ''}
              </td>
              <td class="px-4 py-2 w-32">{formatDuration(row.daysBetween)}</td>
              <td class="px-4 py-2">{row.count}</td>
              <td class="px-4 py-2">{row.wearpercentage}%</td>
              <td class="px-4 py-2 {RESULT_CLASSES[row.result] ?? ''}">{resultLabel(row)}</td>
            {:else}
              <td class="px-4 py-2">{row.sensors}</td>
              <td class="px-4 py-2 {RESULT_CLASSES.passed}">{row.passed}</td>
              <td class="px-4 py-2 {RESULT_CLASSES.failed}">{row.failed}</td>
              <td class="px-4 py-2 {RESULT_CLASSES.failed}">{row.earlyFailures}</td>
              <td class="px-4 py-2 {RESULT_CLASSES.inProgress}">{row.inProgress}</td>
              <td class="px-4 py-2">{formatRate(row.failureRate)}</td>
            {/if}
          </tr>
        {:else}
          <tr class="text-sm text-gray-500 dark:text-gray-400">
            <td class="px-4 py-6 text-center" colspan="10">No CGM sensors found</td>
          </tr>
        {/each}
      </tbody>
    </table>
  </div>
{/if}

<Footer>
  <div></div>
  <div>
    <button
      on:click={handleExportExcel}
      disabled={!report || rows.length === 0}
      class="bg-stone-300 dark:bg-gray-600 text-black dark:text-white py-1 px-4 rounded-full text-xs disabled:opacity-50"
      >Excel</button
    >
  </div>
</Footer>

<style>
  .loader {
    border: 2px solid #20669c;
    border-radius: 50%;
    border-top: 4px solid #98acb4;
    width: 32px;
    height: 32px;
    animation: spin 2s linear infinite;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
</style>
